import { Link } from "react-router-dom";
import { ArrowRight, Star, Sparkles, Award, Globe2, Heart, Quote, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { SERVICES, WHATSAPP_LINK, openWhatsApp } from "@/data/site";
import { api, formatINR } from "@/lib/api";
import HeroCelestial from "@/components/HeroCelestial";
import { useLang } from "@/contexts/LanguageContext";

const HERO_BG = "https://images.unsplash.com/photo-1546104473-d2d1496cebbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxkYXJrJTIwbmlnaHQlMjBza3klMjBnb2xkJTIwY29uc3RlbGxhdGlvbnMlMjBhc3Ryb2xvZ3l8ZW58MHx8fHwxNzgyNTA1MjQ0fDA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG = "https://customer-assets.emergentagent.com/job_dr-tannu-vedic/artifacts/8ljipjvs_IMG_3520.png";

export default function Home() {
    const [testimonials, setTestimonials] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const { t } = useLang();

    const STATS = [
        { v: "9+",  l: t("stats.years") },
        { v: "12K+", l: t("stats.lives") },
        { v: "25+", l: t("stats.countries") },
        { v: "4.9★", l: t("stats.rating") },
    ];

    const EXPERTISE = [
        t("about.exp.bnn"),
        t("about.exp.numerology"),
        t("about.exp.career"),
        t("about.exp.marriage"),
        t("about.exp.foreign"),
        t("about.exp.health"),
        t("about.exp.horoscope"),
        t("about.exp.remedies"),
    ];

    useEffect(() => {
        api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 6))).catch(() => {});
        api.get("/blogs").then((r) => setBlogs(r.data.slice(0, 3))).catch(() => {});
    }, []);

    const featured = SERVICES.slice(0, 6);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[100vh] flex items-center overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 -z-10">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/85 to-ink" />
                    <div className="absolute inset-0 star-field opacity-50" />
                </div>

                <div className="container-vv pt-32 pb-20 grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="gold-line" />
                            <span className="label-eyebrow">{t("hero.eyebrow")}</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] text-cream text-balance">
                            {t("hero.title_a")}{" "}
                            <span className="italic bg-clip-text text-transparent bg-gold-gradient">
                                {t("hero.title_b")}
                            </span>
                        </h1>
                        <p className="mt-4 text-sm tracking-[0.2em] uppercase text-gold/90">{t("hero.tagline")}</p>
                        <p className="mt-8 max-w-2xl text-base lg:text-lg text-cream-muted leading-relaxed">
                            {t("hero.subhead")}
                        </p>

                        <div className="mt-6 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">✦ Vedic Astrologer</span>
                            <span className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">🏅 PhD Gold Medalist</span>
                            <span className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">🏅 MA Vastu Gold Medalist</span>
                            <span className="inline-flex items-center gap-2 text-[10px] tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">BNN Expert</span>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/booking" className="btn-gold text-base" data-testid="hero-book-btn">
                                {t("hero.cta_book")} <ArrowRight className="h-4 w-4" />
                            </Link>
                            <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noreferrer" className="btn-outline-gold text-base" data-testid="hero-whatsapp-btn">
                                {t("hero.cta_whatsapp")}
                            </a>
                        </div>

                        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
                            {STATS.map((s) => (
                                <div key={s.l} className="border-l border-gold/30 pl-4">
                                    <div className="font-serif text-3xl text-cream">{s.v}</div>
                                    <div className="text-xs tracking-widest uppercase text-cream-muted mt-1">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 hidden lg:block">
                        <HeroCelestial />
                    </div>
                </div>
            </section>

            {/* ABOUT TEASER */}
            <section className="section-pad" data-testid="about-teaser">
                <div className="container-vv grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-4 bg-gold/10 blur-3xl rounded-full" />
                            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-gold/30">
                                <img src={ABOUT_IMG} alt="Dr. Tannu Aggarwal" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="gold-line" />
                            <span className="label-eyebrow">{t("about.eyebrow")}</span>
                        </div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight text-balance">
                            {t("about.title")}
                        </h2>
                        <p className="mt-6 text-cream-muted leading-relaxed">
                            {t("about.body")}
                        </p>

                        <div className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                            {EXPERTISE.map((e) => (
                                <div key={e} className="flex items-center gap-2 text-sm text-cream">
                                    <Check className="h-4 w-4 text-gold flex-shrink-0" />
                                    <span>{e}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 flex gap-4">
                            <Link to="/about" className="btn-outline-gold text-sm" data-testid="about-readmore-btn">{t("about.read_more")}</Link>
                            <Link to="/booking" className="btn-gold text-sm" data-testid="about-book-btn">{t("about.book_now")}</Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="section-pad bg-ink-deep/50 border-y border-gold/10" data-testid="services-preview">
                <div className="container-vv">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="gold-line" />
                                <span className="label-eyebrow">{t("services.eyebrow")}</span>
                            </div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream max-w-2xl text-balance">
                                {t("services.title")}
                            </h2>
                        </div>
                        <Link to="/services" className="btn-outline-gold text-sm self-start" data-testid="see-all-services-btn">
                            {t("services.view_all")} <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {featured.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Link key={s.slug} to={`/booking?service=${s.slug}`} className="card-luxe group block" data-testid={`service-card-${s.slug}`}>
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="h-12 w-12 rounded-xl border border-gold/30 grid place-items-center bg-ink-deep/60 group-hover:border-gold transition-colors">
                                            <Icon className="h-5 w-5 text-gold" />
                                        </div>
                                        {s.tag && <span className="text-[10px] tracking-widest uppercase text-gold border border-gold/40 px-2 py-1 rounded-full">{s.tag}</span>}
                                    </div>
                                    <h3 className="font-serif text-2xl text-cream mb-3">{s.name}</h3>
                                    <p className="text-sm text-cream-muted leading-relaxed mb-6 min-h-[60px]">{s.blurb}</p>
                                    <div className="flex items-center justify-between pt-5 border-t border-gold/15">
                                        <div>
                                            <div className="font-serif text-xl text-gold">{formatINR(s.priceInr)}</div>
                                            <div className="text-xs text-cream-muted">{s.duration}</div>
                                        </div>
                                        <span className="text-sm text-gold flex items-center gap-1 group-hover:gap-2 transition-all">{t("services.book_now")} <ArrowRight className="h-3.5 w-3.5" /></span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHY US — values bento */}
            <section className="section-pad" data-testid="why-us-section">
                <div className="container-vv">
                    <div className="text-center max-w-3xl mx-auto mb-14">
                        <div className="label-eyebrow">{t("why.eyebrow")}</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream mt-4 text-balance">
                            {t("why.title")}
                        </h2>
                    </div>

                    {/* Free Kundli + Daily Horoscope — Lead magnets */}
                    <div className="grid md:grid-cols-2 gap-6 mb-14" data-testid="lead-magnets-section">
                        <Link to="/free-kundli" className="group rounded-3xl border border-gold/30 bg-gradient-to-br from-ink-deep to-ink p-8 hover:border-gold/60 transition-all" data-testid="lead-magnet-kundli">
                            <div className="flex items-start gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-gold/15 border border-gold/40 grid place-items-center flex-shrink-0">
                                    <Sparkles className="h-7 w-7 text-gold" />
                                </div>
                                <div>
                                    <div className="label-eyebrow text-xs">100% Free · Instant</div>
                                    <h3 className="font-serif text-2xl text-cream mt-2">Get Your Free Janma Kundli</h3>
                                    <p className="text-sm text-cream-muted mt-2 leading-relaxed">Personalized Vedic report with your rashi, nakshatra, lucky color, gemstone & one remedy — delivered to your inbox in seconds.</p>
                                    <span className="inline-flex items-center gap-1 mt-4 text-sm text-gold group-hover:gap-2 transition-all">Generate Kundli <ArrowRight className="h-3.5 w-3.5" /></span>
                                </div>
                            </div>
                        </Link>
                        <Link to="/daily-horoscope" className="group rounded-3xl border border-gold/30 bg-gradient-to-br from-ink-deep to-ink p-8 hover:border-gold/60 transition-all" data-testid="lead-magnet-horoscope">
                            <div className="flex items-start gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-gold/15 border border-gold/40 grid place-items-center flex-shrink-0">
                                    <Star className="h-7 w-7 text-gold" />
                                </div>
                                <div>
                                    <div className="label-eyebrow text-xs">Daily · All 12 Rashis</div>
                                    <h3 className="font-serif text-2xl text-cream mt-2">Today's Vedic Horoscope</h3>
                                    <p className="text-sm text-cream-muted mt-2 leading-relaxed">Fresh daily readings for all 12 moon signs — love, career, finance & a practical Vedic tip. Subscribe for inbox delivery.</p>
                                    <span className="inline-flex items-center gap-1 mt-4 text-sm text-gold group-hover:gap-2 transition-all">Read today's horoscope <ArrowRight className="h-3.5 w-3.5" /></span>
                                </div>
                            </div>
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { icon: Sparkles, t: "BNN Specialist", d: "Precise predictions using the rare Bhrigu Nandi Nadi system — beyond standard chart reading." },
                            { icon: Globe2, t: "Global Clientele", d: "Online sessions across India, US, UK, UAE, Canada, Australia & more. Pay in INR, attend in your timezone." },
                            { icon: Heart, t: "Ethical Remedies", d: "No fear-based selling. Remedies are simple, chart-specific, and never overpriced." },
                            { icon: Award, t: "7+ Years Practice", d: "Daily readings, continual study, and a track record of 12,000+ satisfied seekers." },
                            { icon: Star, t: "Confidential & Safe", d: "Your details are encrypted & never shared. Sessions follow a GDPR-aligned privacy practice." },
                            { icon: Check, t: "Transparent Pricing", d: "All consultation fees published upfront. No hidden remedies, no upsell pressure." },
                        ].map((v, i) => {
                            const Icon = v.icon;
                            return (
                                <div key={i} className="card-luxe">
                                    <div className="h-12 w-12 rounded-xl bg-gold/10 border border-gold/30 grid place-items-center mb-5">
                                        <Icon className="h-5 w-5 text-gold" />
                                    </div>
                                    <h3 className="font-serif text-xl text-cream mb-2">{v.t}</h3>
                                    <p className="text-sm text-cream-muted leading-relaxed">{v.d}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="section-pad bg-ink-deep/50 border-y border-gold/10" data-testid="testimonials-section">
                <div className="container-vv">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="label-eyebrow">{t("test.eyebrow")}</div>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream mt-4 text-balance">
                            {t("test.title")}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {testimonials.map((t) => (
                            <div key={t.id} className="card-luxe relative" data-testid={`testimonial-${t.id}`}>
                                <Quote className="absolute top-4 right-4 h-6 w-6 text-gold/30" />
                                <div className="flex items-center gap-1 mb-4">
                                    {[...Array(t.rating)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold text-gold" />)}
                                </div>
                                <p className="text-cream text-sm leading-relaxed mb-6">"{t.text}"</p>
                                <div className="pt-4 border-t border-gold/15">
                                    <div className="font-serif text-lg text-cream">{t.name}</div>
                                    <div className="text-xs text-cream-muted mt-0.5">{t.location} · {t.service}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* BLOG PREVIEW */}
            <section className="section-pad" data-testid="blog-preview-section">
                <div className="container-vv">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
                        <div>
                            <div className="label-eyebrow">{t("blog.eyebrow")}</div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream mt-4 text-balance">{t("blog.title")}</h2>
                        </div>
                        <Link to="/blog" className="btn-outline-gold text-sm self-start">{t("blog.read_all")} <ArrowRight className="h-4 w-4" /></Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {blogs.map((b) => (
                            <Link key={b.slug || b.id} to={`/blog/${b.slug}`} className="card-luxe p-0 overflow-hidden block group" data-testid={`blog-card-${b.slug}`}>
                                <div className="aspect-[16/10] overflow-hidden">
                                    <img src={b.cover_url} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="p-6">
                                    <div className="text-[10px] tracking-widest uppercase text-gold mb-3">{b.category}</div>
                                    <h3 className="font-serif text-xl text-cream leading-snug mb-2">{b.title}</h3>
                                    <p className="text-sm text-cream-muted line-clamp-2">{b.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad" data-testid="cta-section">
                <div className="container-vv">
                    <div className="relative overflow-hidden rounded-3xl border border-gold/30 p-12 md:p-20 text-center bg-ink-surface/40 backdrop-blur-md">
                        <div className="absolute inset-0 star-field opacity-30" />
                        <div className="relative">
                            <div className="label-eyebrow">{t("cta.eyebrow")}</div>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream mt-4 max-w-3xl mx-auto text-balance">
                                {t("cta.title")}
                            </h2>
                            <p className="mt-6 text-cream-muted max-w-2xl mx-auto">
                                {t("cta.body")}
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4 justify-center">
                                <Link to="/booking" className="btn-gold" data-testid="cta-book-btn">{t("hero.cta_book")} <ArrowRight className="h-4 w-4" /></Link>
                                <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noreferrer" className="btn-outline-gold" data-testid="cta-whatsapp-btn">{t("hero.cta_whatsapp")}</a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
